import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardTitle } from "@/components/ui/card";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useDeleteTaskMutationHook } from "@/hooks";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsVerticalIcon, DragHandleDots2Icon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TaskDialogContent } from "./Dialogs";
import { Task } from "@/graphql/__generated__/graphql";
import { labelColourMap } from "./constants";
import { useTranslation } from "react-i18next";
import { formatDateLocale } from "@/utils/helpers";
import { useIntl } from "react-intl";
import { UserAvatar } from "../common";
import { cn } from "@/lib/utils";

interface TaskComponentProps {
  task: Task;
  type: "user" | "tender";
  canEdit?: boolean;
  index?: number;
}

/**
 * Component to render a task
 * @param task Task object
 * @param type "user" or "tender" - used to determine the rendering of the component
 * @param canEdit Boolean to determine if the user can edit the tasks
 * @returns JSX.Element
 */
function TaskComponent({ task, type, canEdit }: TaskComponentProps) {
  const { t } = useTranslation(["components", "common"]);
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramTaskId = searchParams.get("taskId");
  const canEditAndInTender = type === "tender" && canEdit;

  // Sortable hook to make the task draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: type === "user" || !canEdit });

  // Animation for dragging a card
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : undefined,
  };

  if (!task || !task.id) {
    console.error("TaskComponent received an invalid task:", task);
    return null; // Safeguard to avoid rendering invalid tasks
  }

  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);

  const { deleteTaskMutation } = useDeleteTaskMutationHook();

  const onViewInTenderClick = () => {
    navigate(`/tender/${task.tenderId}/tasksboard?taskId=${task.id}`);
  };

  // Open the dialog on click
  // Using state so dialog content is not rendered until needed
  // Which is needed to prevent calling queries on all tasks
  const onViewClick = () => {
    setIsOpen(true);
  };

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    deleteTaskMutation({
      variables: { deleteTaskId: task.id }
    });
  };

  const handleDialogChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // If the current query param matches this task, remove it
      if (searchParams.get("taskId") === String(task.id)) {
        searchParams.delete("taskId");
        setSearchParams(searchParams, { replace: true });
      }
    }
  };

  // If the URL has `?taskId=` matching this task, open the dialog automatically.
  useEffect(() => {
    if (paramTaskId && paramTaskId === String(task.id)) {
      setIsOpen(true);
    }
  }, [paramTaskId, task.id]);

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild onClick={onViewClick}>
        {/* ref for determining what is part of the drag and drop intersection */}
        <Card
          ref={setNodeRef}
          style={style}
          className="flex flex-col gap-2 hover:bg-slate-50 cursor-pointer p-4"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex gap-2 items-start">
              {/* Only dragable from following icon */}
              {canEditAndInTender && (
                <DragHandleDots2Icon
                  className="mt-1 icon-xs shrink-0 hover:cursor-grab"
                  {...attributes}
                  {...listeners}
                />
              )}
              <CardTitle className="text-base">{task.title}</CardTitle>
            </div>
            {
              // Show dropdown trigger if:
              // 1. User can edit and is in tender view, OR
              // 2. User is in "My Tasks" view but cannot edit the task
              // Due to certain conditions, there can be no items in the dropdown so this hides the trigger if so.
              ((canEditAndInTender && type === "tender") || (!canEditAndInTender && type === "user")) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="ghost" onClick={(e) => e.stopPropagation()}>
                      <DotsVerticalIcon className="icon-xs" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {
                      canEditAndInTender ? (
                        <>
                          <DropdownMenuItem
                            className="hover:cursor-pointer text-red-400"
                            onClick={onDeleteClick}
                          >{ t("common:buttons.delete") }</DropdownMenuItem>
                        </>
                      ): type === "user" ? (
                        <DropdownMenuItem
                          className="hover:cursor-pointer"
                          onClick={onViewInTenderClick}
                        >{ t("tasksboard.viewinchance") }</DropdownMenuItem>
                      ) : null
                    }
                  </DropdownMenuContent>
                </DropdownMenu>
              )
            }
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <div className="truncate">{task.description}</div>
            <div className="flex-wrap-2">
              {
                task.labels.map((label) => (
                  <Badge key={label.id} variant="outline" className={cn(labelColourMap[label.color])}>
                    {label.name}
                  </Badge>
                ))
              }
            </div>
            <div className="flex-wrap-2">
              {
                task.assignees.map((assignee) => (
                  <div key={assignee.id} title={`${assignee.firstname} ${assignee.lastname}`}>
                    <UserAvatar user={assignee} showTitle={true} size="md"/>
                  </div>
                ))
              }
            </div>
          </div>
          <div className="flex justify-between items-center gap-2">
            <div>
              {
                type === "user" ? (
                  <div className="text-xs text-gray-500">{task.tenderName}</div>
                ) : (
                  <div className="text-xs text-gray-400">{t("tasksboard.task.createdby")} {task.createdBy.firstname} {task.createdBy.lastname}</div>
                )
              }
            </div>
            {
              task.deadline && (
                <div className="text-xs text-gray-500">
                  {formatDateLocale(intl, task.deadline)}
                </div>
              )
            }
          </div>
          {
            type === "user" && (
              <div>
                <div className="text-xs text-gray-400">{t("tasksboard.task.createdby")} {task.createdBy.firstname} {task.createdBy.lastname}</div>
              </div>
            )
          }
        </Card>
      </DialogTrigger>
      {
        isOpen && (
          <TaskDialogContent task={task} type={type} action="update" canEdit={canEdit} onViewInTenderClick={onViewInTenderClick} setIsOpen={setIsOpen} />
        )
      }
    </Dialog>
  );
}
export default TaskComponent;
